import { Component } from "react";
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler";
import StateConstants from "src/states/StateContstants";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./AddressMap.css";
import Marker from "src/components/comparable_homes/Marker";
import UserFeatureHelper from "src/utils/UserFeatureHelper";


interface AddressMapProps {
    stateHandler: EventAdaptingStateHandler;
    className?: string;
}

class AddressMap extends Component<AddressMapProps> {
    static MARKER_ID_KEY = "data-marker-id";
    static MARKER_TYPE_KEY = "data-marker-type";
    stateHandler: EventAdaptingStateHandler;
    mapContainer: HTMLDivElement | null = null;
    map: maplibregl.Map | null = null;
    mainMarker: maplibregl.Marker | null = null;
    comparableMarkers: maplibregl.Marker[] = [];
    initialBounds: maplibregl.LngLatBounds | null = null;

    constructor(props: AddressMapProps) {
        super(props);
        this.stateHandler = props.stateHandler;
        this.state = {
            hoverMarkerId: null,
            valuation: null
        };
        this.addMarkerEventListeners.bind(this);
        this.renderMap = this.renderMap.bind(this);
        this.updateMap = this.updateMap.bind(this);
    }

    componentDidMount() {
        this.stateHandler.addListener(StateConstants.MAP_MARKER_HOVER, (state: any) => {
            this.setState({
                hoverMarkerId: state
            });
            this.updateMarkerHighlight(state ?? null);
        });
        this.stateHandler.addListener(StateConstants.REMOVED_HOMES, (state: any) => {
            this.setState({
                removedHomes: state
            });
            this.addComparableMarkers();
        });
        this.stateHandler.addListener(StateConstants.USER_FEATURES, () => {
            this.addComparableMarkers();
        });
        setTimeout(() => {
            this.initializeMap();
        }, 0);
    }

    componentDidUpdate() {
        this.updateMap();
    }

    componentWillUnmount() {
        if (this.map) {
            this.map = null;
            this.mainMarker = null;
            this.comparableMarkers = [];
        }
    }

    initializeMap() {
        const valuation = this.stateHandler.get(StateConstants.VALUATION_STATE);
        if (!valuation || !valuation["gc"]) return;

        const { lt: latitude, ln: longitude } = valuation["gc"];
        if (!latitude || !longitude) return;

        if (!this.map) {
            this.renderMap(latitude, longitude);
        }
    }

    updateMarkerHighlight(highlightedId: string | null) {
        this.comparableMarkers.forEach((marker) => {
            const el = marker.getElement();
            const id = el.getAttribute(AddressMap.MARKER_ID_KEY);
            if (id === highlightedId) {
                el.classList.add('highlighted-marker');
            } else {
                el.classList.remove('highlighted-marker');
            }
        });
    }

    addMarkerEventListeners() {
        this.comparableMarkers.forEach(marker => {
            if (marker.getElement().getAttribute('data-locked') === 'true') return;
            const markerId = marker.getElement().getAttribute(AddressMap.MARKER_ID_KEY);
            marker.getElement().addEventListener('click', () => {
                this.comparableMarkers.forEach(m => m.getElement().classList.remove('highlighted-marker'));
                this.stateHandler.set(StateConstants.MAP_MARKER_CLICK, markerId);
                this.stateHandler.set(StateConstants.MAP_MARKER_HOVER, markerId);
                marker.getElement().classList.add('highlighted-marker');
            });
            marker.getElement().addEventListener('touchstart', () => {
                this.comparableMarkers.forEach(m => m.getElement().classList.remove('highlighted-marker'));
                this.stateHandler.set(StateConstants.MAP_MARKER_CLICK, markerId);
                this.stateHandler.set(StateConstants.MAP_MARKER_HOVER, markerId);
                marker.getElement().classList.add('highlighted-marker');
            });
            marker.getElement().addEventListener('mouseenter', () => {
                if (this.stateHandler.get(StateConstants.MAP_MARKER_CLICK) !== marker.getElement().getAttribute(AddressMap.MARKER_ID_KEY)) {
                    this.comparableMarkers.forEach(m => m.getElement().classList.remove('highlighted-marker'));
                    this.stateHandler.set(StateConstants.MAP_MARKER_CLICK, null);
                }
                this.stateHandler.set(StateConstants.MAP_MARKER_HOVER, marker.getElement().getAttribute(AddressMap.MARKER_ID_KEY));
                marker.getElement().classList.add('highlighted-marker');
            });
            marker.getElement().addEventListener('mouseleave', () => {
                if (this.stateHandler.get(StateConstants.MAP_MARKER_CLICK) === marker.getElement().getAttribute(AddressMap.MARKER_ID_KEY)) {
                    return;
                }
                this.stateHandler.set(StateConstants.MAP_MARKER_HOVER, null);
                this.stateHandler.set(StateConstants.MAP_MARKER_CLICK, null);
                this.comparableMarkers.forEach(m => m.getElement().classList.remove('highlighted-marker'));
            });
        });
    }

    renderMap(latitude: number, longitude: number) {
        const valuation = this.stateHandler.get(StateConstants.VALUATION_STATE);
        const isGoogleBot = valuation && valuation["metadata"]["gb"];
        if (isGoogleBot) {
            // Do not render map for google bots as it is expensive and not neccesary for indexing.
            return;
        } 
        if (!this.mapContainer) return;

        if (this.mapContainer.offsetWidth === 0 || this.mapContainer.offsetHeight === 0) {
            setTimeout(() => {
                this.renderMap(latitude, longitude);
            }, 100);
            return;
        }

        this.mapContainer.innerHTML = '';

        try {
            this.map = new maplibregl.Map({
                container: this.mapContainer,
                style: {
                    version: 8,
                    sources: {
                        'osm': {
                            type: 'raster',
                            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                            tileSize: 256,
                            attribution: '© OpenStreetMap contributors'
                        }
                    },
                    layers: [
                        {
                            id: 'osm-tiles',
                            type: 'raster',
                            source: 'osm',
                            minzoom: 0,
                            maxzoom: 19
                        }
                    ]
                },
                center: [longitude, latitude],
                zoom: 17,
                attributionControl: { compact: false },
                cooperativeGestures: true
            });

            this.mainMarker = new maplibregl.Marker({
                element: Marker.createMarkerElement("", true, latitude)
            })
                .setLngLat([longitude, latitude])
                .addTo(this.map);

            this.mainMarker.getElement().setAttribute(AddressMap.MARKER_TYPE_KEY, 'main');
            this.addComparableMarkers();

            setTimeout(() => {
                if (this.map) {
                    this.map.resize();
                }
            }, 100);
        } catch (error) {
            console.error('Error initializing map:', error);
        }
    }

    addComparableMarkers() {
        if (!this.map) return;

        this.comparableMarkers.forEach(marker => marker.remove());
        this.comparableMarkers = [];

        const comparableHomes = this.stateHandler.get(StateConstants.COMPARABLE_HOMES_STATE);
        const valuation = this.stateHandler.get(StateConstants.VALUATION_STATE);
        if (!comparableHomes || !comparableHomes.comparable_addresses) return;

        const canSeeAll = UserFeatureHelper.hasFeature(this.stateHandler, UserFeatureHelper.ALL_COMPARABLES);
        let visibleCount = 0;
        const bounds = new maplibregl.LngLatBounds();

        // Add main property to bounds
        if (valuation && valuation["gc"] && valuation["gc"].lt && valuation["gc"].ln) {
            bounds.extend([valuation["gc"].ln, valuation["gc"].lt]);
        }
        comparableHomes.comparable_addresses.forEach((comparableHome: any) => {
            let isVisible = true;
            if ("sv" in comparableHome && comparableHome.sv === false) {
                isVisible = false;
            }
            if (comparableHome.gc && comparableHome.gc.lt && comparableHome.gc.ln && isVisible) {
                const { lt: latitude, ln: longitude } = comparableHome.gc;

                let isMainMarker = false;
                if (comparableHome.id === valuation["id"]) {
                    isMainMarker = true;
                }

                const isLocked = !canSeeAll && visibleCount >= 1;
                visibleCount++;

                const marker = new maplibregl.Marker({
                    element: Marker.createMarkerElement(comparableHome.id, isMainMarker, latitude, isLocked)
                })
                    .setLngLat([longitude, latitude])
                    .addTo(this.map!);

                marker.getElement().setAttribute(AddressMap.MARKER_TYPE_KEY, 'comparable');
                marker.getElement().setAttribute(AddressMap.MARKER_ID_KEY, comparableHome.id);
                if (isLocked) {
                    marker.getElement().setAttribute('data-locked', 'true');
                }
                this.comparableMarkers.push(marker);

                // Add this marker's coordinates to bounds
                bounds.extend([longitude, latitude]);
            }
        });
        
        // Fit map to show all markers with some padding
        if (this.map && !bounds.isEmpty()) {
            this.map.fitBounds(bounds, {
                padding: 50,
                maxZoom: 17
            });
            if (!this.initialBounds) {
                this.initialBounds = bounds;
            }
        }
        
        this.addMarkerEventListeners();
        const currentHoverId = this.stateHandler.get(StateConstants.MAP_MARKER_HOVER);
        this.updateMarkerHighlight(currentHoverId ?? null);
    }

    updateMap() {
        const valuation = this.stateHandler.get(StateConstants.VALUATION_STATE);
        if (valuation && valuation["gc"]) {
            const { lt: latitude, ln: longitude } = valuation["gc"];
            if (latitude && longitude && !this.map) {
                this.renderMap(latitude, longitude);
            }
        }
    }

    render() {
        return (
            <div className="property-map-container">
                <div ref={(el) => this.mapContainer = el} className="map-wrapper">
                    <div className="map-loading">
                        Map ...
                    </div>
                </div>
                <div className="map-controls">
                    <button
                        className="map-control-btn"
                        aria-label="Zoom in"
                        onClick={() => this.map?.zoomIn()}
                    >+</button>
                    <button
                        className="map-control-btn"
                        aria-label="Zoom out"
                        onClick={() => this.map?.zoomOut()}
                    >−</button>
                    <button
                        className="map-control-btn map-control-home"
                        aria-label="Reset view"
                        onClick={() => {
                            if (this.map && this.initialBounds) {
                                this.map.fitBounds(this.initialBounds, { padding: 50, maxZoom: 17 });
                            }
                        }}
                    >⌂</button>
                </div>
            </div>
        );
    }
}

export default AddressMap; 